import { Element } from 'slate';

import { serializePdfTableToPreviewHtml } from '../../serializers/html';
import type { DocParts, TableElement } from '../../slate-api';
import { AlineTransforms, isParagraphElement, isTableElement, Queries } from '../../slate-api';
import type {
  ApryseBlockElement,
  ApryseDoc,
  ApryseList,
  ApryseListItem,
  ApryseTable,
} from '../../types/extraction';
import { generateLocationAnchor } from '../pdf/locationAnchor';

/**
 * A table or table-row anchor extracted from a document, keyed by the same
 * location anchor the LLM-facing document format serializes (unprefixed): the
 * element's `locationId` for DOCX, its `{pageNum}:{x1}:{y1}:{x2}:{y2}` rect
 * for PDF.
 */
export type ExtractedBlockAnchor = {
  kind: 'row' | 'table';
  locationAnchor: string;
  /**
   * Ready-to-render preview `<table>` HTML for the chat card, populated only
   * for PDF-origin anchors. DOCX suggestion cards derive their preview from
   * the applied edit's removed content; the PDF branch never touches an
   * editor, so its preview is precomputed here (null for DOCX).
   */
  pdfPreviewHtml: string | null;
};

/**
 * Extracts table and row anchors from a DOCX (Aline) document — the
 * block-anchor counterpart of `extractParagraphsFromAlineDoc`. Walks the same
 * active roots (body plus active headers/footers); tables or rows without a
 * `locationId` (documents not yet migrated to schema v3) are skipped, since
 * the serializer emits no id for them either.
 */
export function extractBlockAnchorsFromAlineDoc(docParts: DocParts): ExtractedBlockAnchor[] {
  const editor = AlineTransforms.createQueryEditorFromJson(docParts);
  const rootEntries = Queries.getActiveRootEntries(editor);

  const anchors: ExtractedBlockAnchor[] = [];
  for (const [rootElement] of rootEntries) {
    for (const child of rootElement.children) {
      if (Element.isElement(child)) collectFromAlineElement(child, anchors);
    }
  }
  return anchors;
}

function collectFromAlineElement(element: Element, anchors: ExtractedBlockAnchor[]): void {
  if (isTableElement(element)) {
    collectFromAlineTable(element, anchors);
    return;
  }
  for (const child of element.children) {
    if (Element.isElement(child)) collectFromAlineElement(child, anchors);
  }
}

function collectFromAlineTable(table: TableElement, anchors: ExtractedBlockAnchor[]): void {
  if (table.locationId) {
    anchors.push({ kind: 'table', locationAnchor: table.locationId, pdfPreviewHtml: null });
  }
  for (const row of table.children) {
    if (!row.locationId) continue;
    anchors.push({ kind: 'row', locationAnchor: row.locationId, pdfPreviewHtml: null });
  }

  // Nested tables get their own anchors too.
  for (const row of table.children) {
    for (const cell of row.children) {
      for (const block of cell.children) {
        if (Element.isElement(block) && !isParagraphElement(block)) {
          collectFromAlineElement(block, anchors);
        }
      }
    }
  }
}

/**
 * Extracts table and row anchors from a PDF (Apryse) document — the
 * block-anchor counterpart of `extractParagraphsFromPdfDoc`. Anchors are the
 * elements' bounding rects via `generateLocationAnchor`, matching the anchors
 * `extractFromApryseParagraph` assigns to the paragraphs inside.
 */
export function extractBlockAnchorsFromPdfDoc(doc: ApryseDoc): ExtractedBlockAnchor[] {
  return doc.pages.flatMap(page =>
    page.elements.flatMap(element => collectFromApryseElement(element, page.properties.pageNumber))
  );
}

function collectFromApryseElement(
  element: ApryseBlockElement,
  pageNumber: number
): ExtractedBlockAnchor[] {
  switch (element.type) {
    case 'table':
      return collectFromApryseTable(element, pageNumber);
    case 'group':
    case 'header':
    case 'footer':
      return element.contents.flatMap(child => collectFromApryseElement(child, pageNumber));
    default:
      return [];
  }
}

function collectFromApryseTable(table: ApryseTable, pageNumber: number): ExtractedBlockAnchor[] {
  const cellTextsByRow = table.trs.map(row =>
    row.tds.map(cell =>
      cell.contents
        .map(collectApryseText)
        .filter(text => text !== '')
        .join('\n')
    )
  );

  const anchors: ExtractedBlockAnchor[] = [
    {
      kind: 'table',
      locationAnchor: generateLocationAnchor(pageNumber, table.rect),
      pdfPreviewHtml: serializePdfTableToPreviewHtml(cellTextsByRow),
    },
  ];
  table.trs.forEach((row, rowIndex) => {
    anchors.push({
      kind: 'row',
      locationAnchor: generateLocationAnchor(pageNumber, row.rect),
      pdfPreviewHtml: serializePdfTableToPreviewHtml(
        cellTextsByRow[rowIndex] === undefined ? [] : [cellTextsByRow[rowIndex]]
      ),
    });
  });

  // Nested tables inside cells get their own anchors too.
  const nested = table.trs.flatMap(row =>
    row.tds.flatMap(cell =>
      cell.contents.flatMap(child => collectFromApryseElement(child, pageNumber))
    )
  );
  return [...anchors, ...nested];
}

/**
 * The plain text of one Apryse cell element, mirroring which elements
 * `extractFromApryseBlockElement` turns into extracted paragraphs
 * (paragraphs, headings, text lines, textbox paragraphs, and list items).
 */
function collectApryseText(element: ApryseBlockElement): string {
  switch (element.type) {
    case 'paragraph':
    case 'heading': {
      const { contents } = element;
      if (contents === undefined) return '';
      if (typeof contents === 'string') return contents;
      return contents.map(span => span.text).join('');
    }
    case 'textLine':
      return element.text;
    case 'textbox':
      return element.contents.map(collectApryseText).join('\n');
    case 'list':
      return element.items.map(collectApryseListItemText).join('\n');
    case 'group':
    case 'header':
    case 'footer':
      return element.contents.map(collectApryseText).join('\n');
    default:
      return '';
  }
}

function collectApryseListItemText(item: ApryseList | ApryseListItem): string {
  if (item.type === 'list') {
    return item.items.map(collectApryseListItemText).join('\n');
  }
  return item.body.contents.map(collectApryseText).join('\n');
}
